import React, { useCallback } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeselectIcon from '@mui/icons-material/Deselect';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SouthIcon from '@mui/icons-material/South';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { reorderWellLocationOnDeckItem } from 'client/app/components/Parameters/PlateLayout/lib/WellAvailability';
import { usePlateLayoutEditorContext } from 'client/app/components/Parameters/PlateLayout/PlateLayoutEditorProvider';
import { shuffle } from 'common/lib/arrays';
import { WellSortMode } from 'common/types/plateAssignments';
import { getAllPlateWells } from 'common/types/plateType';
import Button from 'common/ui/components/Button';
import IconWithPopover from 'common/ui/components/IconWithPopover';

export function DescriptiveTools() {
  const { selectedWells, setSelectedWells, plateType, selectedLiquidOrLayerId } =
    usePlateLayoutEditorContext();

  const selectAll = useCallback(() => {
    if (!plateType) {
      return;
    }

    setSelectedWells(
      getAllPlateWells(plateType, (col, row) => ({
        col,
        row,
        deck_item_id: plateType.id,
      })),
    );
  }, [plateType, setSelectedWells]);

  const isLayerSelected = selectedLiquidOrLayerId !== undefined;
  const areNoWellsSelected = selectedWells.length === 0;
  const areAllWellsSelected =
    plateType && selectedWells.length === plateType.rows * plateType.columns;

  return (
    <Stack gap={4} margin="auto">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        gap={4}
      >
        <Typography variant="h5">Selection tools: </Typography>
        <Button
          variant="secondary"
          size="small"
          startIcon={<SelectAllIcon />}
          onClick={selectAll}
          disabled={areAllWellsSelected}
        >
          Select All
        </Button>
        <Button
          variant="secondary"
          size="small"
          startIcon={<DeselectIcon />}
          disabled={areNoWellsSelected}
          onClick={() => setSelectedWells([])}
        >
          Deselect
        </Button>
        <>
          <Divider orientation="vertical" flexItem />
          <Button
            variant="secondary"
            size="small"
            startIcon={<ArrowForwardIcon />}
            disabled={areNoWellsSelected}
            onClick={() =>
              setSelectedWells(
                reorderWellLocationOnDeckItem([...selectedWells], WellSortMode.BY_ROW),
              )
            }
          >
            Row-wise
          </Button>
          <Button
            variant="secondary"
            size="small"
            startIcon={<ArrowDownwardIcon />}
            disabled={areNoWellsSelected}
            onClick={() =>
              setSelectedWells(
                reorderWellLocationOnDeckItem([...selectedWells], WellSortMode.BY_COLUMN),
              )
            }
          >
            Column-wise
          </Button>
          <Button
            variant="secondary"
            size="small"
            startIcon={<ShuffleIcon />}
            disabled={areNoWellsSelected}
            onClick={() => setSelectedWells(shuffle([...selectedWells]))}
          >
            Randomize
          </Button>
          <IconWithPopover
            icon={<HelpIcon color="inherit" />}
            popoverContent={
              <Typography variant="caption">
                When groups of tagged liquids are selected, members of the group will fill
                the wells in the number order shown. Row-wise, column-wise and randomize
                options enable quick reordering of this selection.
              </Typography>
            }
          />
        </>
      </Stack>
      <SelectWellsMessage
        visibility={areNoWellsSelected && !isLayerSelected ? 'visible' : 'hidden'}
      >
        <SouthIcon fontSize="small" />
        To add a liquid, begin by selecting wells
      </SelectWellsMessage>
    </Stack>
  );
}

const SelectWellsMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(3),
}));
